import type { Activity, Company, Contact, Opportunity, Proposal } from "../../types";
import { computeWarnings } from "../../lib/businessRules";
import { money, formatDate, daysSince } from "../../lib/format";
import { PriorityBadge } from "../ui/Badge";
import { Avatar } from "../ui/Avatar";

export function OpportunityCard({
  opportunity,
  company,
  activities,
  proposals,
  contacts,
  now,
  draggable,
  onDragStart,
  onDragEnd,
  onOpen,
}: {
  opportunity: Opportunity;
  company: Company | undefined;
  activities: Activity[];
  proposals: Proposal[];
  contacts: Contact[];
  now: Date;
  draggable: boolean;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
  onOpen: () => void;
}) {
  const warnings = computeWarnings(opportunity, activities, proposals, contacts, now);
  const days = daysSince(
    activities.filter((a) => a.opportunityId === opportunity.id).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]?.timestamp ?? opportunity.createdAt,
    now
  );

  return (
    <article
      class="opp-card"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-ai-id={`open-opportunity-${opportunity.id}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <div class="opp-card__top">
        <span class="opp-card__company">{company?.name ?? "Unknown company"}</span>
        <span class="opp-card__prio">
          <PriorityBadge priority={opportunity.priority} />
        </span>
      </div>
      <h3 class="opp-card__name">{opportunity.name}</h3>
      <div class="opp-card__value">{money(opportunity.value)}</div>
      <div class="opp-card__meta">
        <span class="opp-card__owner">
          <Avatar name={opportunity.salesOwner} size="sm" />
          <span>{opportunity.salesOwner}</span>
        </span>
        <span class="opp-card__prob">{opportunity.probability}%</span>
      </div>
      <div class="opp-card__sub">
        <span>Close {formatDate(opportunity.expectedCloseDate)}</span>
        <span>{days !== null ? `${days}d idle` : "no activity"}</span>
      </div>
      {warnings.length > 0 && (
        <div class="opp-card__warnings">
          {warnings.map((w) => (
            <span key={w.code} class={`warn-chip warn-chip--${w.severity === "danger" ? "danger" : w.severity === "info" ? "info" : "warning"}`}>
              <span class="warn-chip__dot" />
              {w.label}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
